import { v4 as uuidv4 } from "uuid";
import { FilterValues } from "../../../data-model/filterValues";
import { mergeNycbsSearchFiltersWithSidebarFilters, combineNycbsApiFilters } from "./buildNycbsSearchFilters";
import {
  NYCBS_EXTERNAL_DEFAULT_ORDER_BY,
  NycbsExternalFilterPayload,
} from "./nycbsExternalFilterPayload";
import { processFilterData } from "./processFilterData";

/**
 * Builds the NYCBS External table API payload for filter and search requests.
 *
 * @param validFilters - Active sidebar filters for this table
 * @param searchTerm - Current search text (empty when not searching)
 * @param dateFromStart - Default range start date (YYYYMMDD)
 * @param dateFromEnd - Default range end date (YYYYMMDD)
 * @param pageSize - Number of rows per page
 * @param orgId - BigQuery organization id (Non-STAT or STAT)
 * @param page - Page number to request (1-indexed)
 * @return NycbsExternalFilterPayload: Request body for the BigQuery API
 */
export const buildNycbsExternalFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
  orgId: string,
  page: number = 1,
): NycbsExternalFilterPayload => {
  const { filters: apiFilters, structuredFilter, dateRange } =
    processFilterData(validFilters);
  const combinedFilters = combineNycbsApiFilters(apiFilters, structuredFilter);

  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasFilters = validFilters.length > 0;
  const omitDates = hasSearchTerm && !hasFilters;

  const datePayload = omitDates
    ? {}
    : {
        date_from_filename_start: dateRange?.startDate ?? dateFromStart,
        date_from_filename_end: dateRange?.endDate ?? dateFromEnd,
      };

  if (hasSearchTerm) {
    return {
      org_id: orgId,
      ...datePayload,
      case_sensitive: false,
      filters: mergeNycbsSearchFiltersWithSidebarFilters(
        searchTerm,
        apiFilters,
        structuredFilter,
      ),
      page,
      page_size: pageSize,
      request_id: uuidv4(),
      order_by: NYCBS_EXTERNAL_DEFAULT_ORDER_BY,
    };
  }

  return {
    org_id: orgId,
    ...datePayload,
    filters: combinedFilters,
    page,
    page_size: pageSize,
    request_id: uuidv4(),
    order_by: NYCBS_EXTERNAL_DEFAULT_ORDER_BY,
  };
};
